import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { loadFavIds } from "@/components/chat/ElitePicks";
import { Input } from "@/components/ui/input";
import { ArrowLeft, BookOpen, Building2, Heart, HelpCircle, MapPin, Search } from "lucide-react";

let schoolsCache = null;
let programsCache = null;
const cached = (key) => {
  const path = key === "s" ? "/catalog/schools" : "/catalog/programs";
  const get = () => api.get(path).then((r) => r.data).catch(() => []);
  if (key === "s") return (schoolsCache = schoolsCache || get());
  return (programsCache = programsCache || get());
};

function ProgramStep({ school, programs, onPick, onBack }) {
  return (
    <div className="w-full max-w-md mt-5 text-left" data-testid="predictor-program-picker">
      <button onClick={onBack} data-testid="predictor-program-back" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-navy mb-2">
        <ArrowLeft className="h-3.5 w-3.5" /> All schools
      </button>
      <p className="text-sm font-bold text-navy mb-2">Which program at {school.title}?</p>
      <div className="max-h-64 overflow-y-auto rounded-2xl border border-border bg-white divide-y divide-border">
        {programs.map((p) => (
          <button key={p.external_id} onClick={() => onPick(p)} data-testid={`predictor-program-${p.external_id}`}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-orange/5 transition-colors">
            <BookOpen className="h-4 w-4 text-orange shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-navy truncate">{p.title}</span>
              <span className="block text-[11px] text-muted-foreground truncate">{[p.level, p.duration].filter(Boolean).join(" · ")}</span>
            </span>
          </button>
        ))}
      </div>
      <button onClick={() => onPick(school)} data-testid="predictor-program-unknown"
        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-navy/30 py-2.5 text-sm font-semibold text-navy hover:border-orange hover:text-orange transition-colors">
        <HelpCircle className="h-4 w-4" /> Not sure which program yet
      </button>
    </div>
  );
}

export function SchoolPicker({ canFav, onPick, onSkip }) {
  const [schools, setSchools] = useState([]);
  const [favs, setFavs] = useState(new Set());
  const [q, setQ] = useState("");
  const [programs, setPrograms] = useState([]);
  const [school, setSchool] = useState(null);

  useEffect(() => {
    cached("s").then(setSchools);
    cached("p").then(setPrograms);
    if (canFav) loadFavIds().then((s) => setFavs(new Set(s)));
  }, [canFav]);

  const schoolPrograms = school ? programs.filter((p) => p.school_id === school.external_id) : [];
  const chooseSchool = (s) => {
    if (programs.some((p) => p.school_id === s.external_id)) setSchool(s);
    else onPick(s);
  };
  if (school) return <ProgramStep school={school} programs={schoolPrograms} onPick={onPick} onBack={() => setSchool(null)} />;

  const ql = q.trim().toLowerCase();
  const shown = schools
    .filter((s) => !ql || [s.title, s.location, s.category].some((v) => v && v.toLowerCase().includes(ql)))
    .sort((a, b) => (favs.has(b.external_id) ? 1 : 0) - (favs.has(a.external_id) ? 1 : 0));

  return (
    <div className="w-full max-w-md mt-5 text-left" data-testid="predictor-school-picker">
      <p className="text-sm font-bold text-navy mb-2">Which school do you want to get into?</p>
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input data-testid="predictor-school-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a school or city…" className="h-10 pl-8 rounded-xl bg-white" />
      </div>
      <div className="max-h-64 overflow-y-auto rounded-2xl border border-border bg-white divide-y divide-border">
        {shown.map((s) => (
          <button key={s.external_id} onClick={() => chooseSchool(s)} data-testid={`predictor-school-${s.external_id}`}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-orange/5 transition-colors">
            <Building2 className="h-4 w-4 text-orange shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-navy truncate">{s.title}</span>
              {s.location && <span className="flex items-center gap-1 text-[11px] text-muted-foreground truncate"><MapPin className="h-3 w-3 shrink-0" />{s.location}</span>}
            </span>
            {favs.has(s.external_id) && <Heart className="h-3.5 w-3.5 text-orange shrink-0" fill="currentColor" />}
          </button>
        ))}
        {shown.length === 0 && <p className="px-3 py-3 text-sm text-muted-foreground">No school matches your search.</p>}
      </div>
      <button onClick={onSkip} data-testid="predictor-school-unknown"
        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-navy/30 py-2.5 text-sm font-semibold text-navy hover:border-orange hover:text-orange transition-colors">
        <HelpCircle className="h-4 w-4" /> I don't know yet
      </button>
    </div>
  );
}
